<script setup lang="ts">
/**
 * Injects Meta Pixel (fbq) on client-side mount.
 * Follows Meta's official base code implementation exactly:
 * - n.queue.push(arguments) — NOT push.bind (avoids CreateListFromArrayLike error)
 * - Wrapped in onMounted for SSR safety
 * - Guarded against double init (if w.fbq already exists)
 *
 * Consent gate: Pixel is blocked until Cookiebot confirms marketing consent
 * (CookiebotOnAccept event). Falls back to immediate init when Cookiebot is
 * not loaded (e.g. local dev without the banner).
 */
const config = useRuntimeConfig()
const PIXEL_ID = config.public.metaPixelId as string

const initPixel = () => {
  if (!PIXEL_ID) return

  const w = window as any
  if (w.fbq) return

  const n = w.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  }
  w._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const t = document.createElement('script')
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(t)

  w.fbq('init', PIXEL_ID)
  w.fbq('track', 'PageView')
}

onMounted(() => {
  if (!PIXEL_ID) return

  const w = window as any
  const hasConsent = !!w.Cookiebot?.consent?.marketing

  if (hasConsent) {
    initPixel()
    return
  }

  const onAccept = () => {
    if (w.Cookiebot?.consent?.marketing) {
      initPixel()
      window.removeEventListener('CookiebotOnAccept', onAccept)
    }
  }
  window.addEventListener('CookiebotOnAccept', onAccept)
  onUnmounted(() => window.removeEventListener('CookiebotOnAccept', onAccept))
})
</script>

<template>
  <div />
</template>
